// Price chart for the per-game detail page. Renders bubbles for every
// analytics trade on a 0-100¢ yes-axis for the selected team. Optionally
// overlays a live WS-feed line (one per enabled venue) when mode='live'.
//
// Bubble layout
//   Y-axis = price in cents (0 = "selected team loses for free",
//           100 = "selected team wins, paid full price").
//   YES trades (on the selected-team viewpoint) plot at their fill price;
//   NO trades plot at theirs. The two clouds form mirrored paths roughly
//   summing to 100 — final-state games have YES near 100 and NO near 0.
//
// Bubble visuals
//   hue    = venue   (cyan = Kalshi, purple = Polymarket)
//   shade  = side    (bright = YES, dark = NO — solid hex, not alpha,
//                     because lightweight-charts markers stack overlapping
//                     alpha back toward full opacity so a 0.45-alpha NO
//                     looked identical to a full-alpha YES on the chart
//                     even though the legend dot rendered correctly faded)
//   size   = volume — radius scales with the trade-volume percentile in
//                     the dataset. Drawn via a custom ISeriesPrimitive
//                     instead of createSeriesMarkers because the built-in
//                     marker plugin caps circles at ~25px and snaps to
//                     ~8 odd-integer pixel sizes, killing variation.
//                     See BubblesPrimitive below.
//   shape  = always a filled dot
//
// Cross-venue ticker quirk: Polymarket only has one ticker per game,
// named after one team. When the user selects the "other" team, we flip
// the side label (YES <-> NO) so Poly trades still bucket sensibly. See
// helpers.ts:projectTradeForTeam for the projection rules.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createChart,
  CrosshairMode,
  LineSeries,
  LineStyle,
  TickMarkType,
  type IChartApi,
  type IPrimitivePaneRenderer,
  type IPrimitivePaneView,
  type ISeriesApi,
  type ISeriesPrimitive,
  type SeriesAttachedParameter,
  type Time,
} from 'lightweight-charts';

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'] as const;
import type { CanvasRenderingTarget2D } from 'fancy-canvas';
import { useMarketStore } from '@/stores/marketStore';
import type { GameRow } from '@/views/ValueDashboard/types';
import type { TradeRow } from '@/services/hydraAnalytics';
import {
  earliestEventStart,
  projectTradeForTeam,
  type Venue,
} from './helpers';

export type ChartMode = 'archived' | 'live';

interface Props {
  mode: ChartMode;
  /** All trades from the analytics fetch — already filtered to marketType. */
  trades: TradeRow[];
  /** Selected team code (uppercase). Drives both bubble projection and
   *  which live-line tickers we sample from marketStore. Ignored when
   *  lineTickers is set (spread/total mode). */
  selectedTeam: string;
  bothTeams: readonly [string, string];
  enabledVenues: ReadonlySet<Venue>;
  /** May be null when marketStore hasn't ingested this game (archived
   *  games rarely have a live frame). */
  game: GameRow | null;
  /** Spread/total mode: filter bubbles to the selected line's ticker(s)
   *  per venue, and sample the live WS line off those tickers via
   *  marketStore.getMarketPrice. Null in moneyline mode (the team-based
   *  projection + GameRow.kalshi/polymarket sampling stays in charge). */
  lineTickers?: { kalshi: string | null; polymarket: string | null } | null;
}

// Color tokens — kept here rather than in Tailwind config so the
// lightweight-charts canvas (which doesn't see Tailwind) and the JSX
// legend share the same hex/rgba strings.
const COLOR = {
  kalshi: {
    yes: '#22d3ee',  // cyan-400 — bright
    no: '#0e7490',   // cyan-700 — same hue, darker
    live: '#22d3ee',
  },
  poly: {
    yes: '#a855f7',  // purple-500 — bright
    no: '#6b21a8',   // purple-800 — same hue, darker
    live: '#a855f7',
  },
};

type SeriesKey = 'kalshi-yes' | 'kalshi-no' | 'poly-yes' | 'poly-no';

function venueOf(k: SeriesKey): Venue {
  return k.startsWith('kalshi') ? 'kalshi' : 'polymarket';
}

function colorOf(k: SeriesKey): string {
  switch (k) {
    case 'kalshi-yes': return COLOR.kalshi.yes;
    case 'kalshi-no': return COLOR.kalshi.no;
    case 'poly-yes': return COLOR.poly.yes;
    case 'poly-no': return COLOR.poly.no;
  }
}

// Dedup data points by second-bin within each series. Lightweight-charts
// rejects duplicate or out-of-order time keys. Within a bin we keep the
// LAST trade's price (so the visualization reflects the most recent
// price) but SUM the sizes — a bubble's size encodes total volume that
// landed in that second, so a flurry of small trades draws as big as one
// whale trade of the same combined volume.
function binBySecond(
  points: Array<{ t: number; price: number; size: number }>,
): Array<{ time: Time; value: number; size: number }> {
  const bySec = new Map<number, { price: number; size: number }>();
  for (const p of points) {
    const sec = Math.floor(p.t / 1000);
    const existing = bySec.get(sec);
    if (existing) {
      existing.price = p.price;
      existing.size += p.size;
    } else {
      bySec.set(sec, { price: p.price, size: p.size });
    }
  }
  return Array.from(bySec.entries())
    .sort(([a], [b]) => a - b)
    .map(([sec, { price, size }]) => ({ time: sec as Time, value: price, size }));
}

// Bubble-radius scaling: pooled-fraction based.
//
// Both Kalshi and Polymarket store `size` as contract count in hydra's
// trades table (Polymarket USD notional is computed as `(price/100) *
// size`, confirming `size` is shares), so it's apples-to-apples to
// pool all four series into one total and size each bubble by its
// fraction of that total.
//
//   radius = MIN + (MAX - MIN) * pow(bin_size / total_size, CURVE)
//
// The CURVE exponent (0..1) is what makes "mostly small bubbles, big
// trades stand out." At CURVE=0.35, a typical trade fraction of ~1e-4
// renders near MIN, the 1% trades sit mid-range, and the rare whales
// at 10%+ of total game volume hit MAX. Lower CURVE → more emphasis on
// whales; higher → more uniform sizing.
//
// Because the BubblesPrimitive draws to a raw canvas with anti-aliased
// subpixel fill, every distinct radius is visually distinct (no
// integer-pixel quantization like the built-in marker plugin imposes).
// MIN_R picked so even the smallest bubble has a few pixels of radius
// to fade through — at MIN_R=1.25 (2.5px diameter) the opaque-to-clear
// gradient compressed into one anti-aliased pixel, making tiny bubbles
// look indistinguishable from solid dots.
const BUBBLE_MIN_R = 3;     // 6px diameter — visible soft halo
const BUBBLE_MAX_R = 24;    // 48px diameter — chunky whale
const BUBBLE_CURVE = 0.4;

function bubbleRadius(binSize: number, totalSize: number): number {
  if (totalSize <= 0 || binSize <= 0) return BUBBLE_MIN_R;
  const fraction = Math.min(1, binSize / totalSize);
  const curved = Math.pow(fraction, BUBBLE_CURVE);
  return BUBBLE_MIN_R + (BUBBLE_MAX_R - BUBBLE_MIN_R) * curved;
}

// ── BubblesPrimitive ──────────────────────────────────────────────
// Custom series primitive that draws filled circles at exact subpixel
// radii via the canvas API. Replaces createSeriesMarkers because the
// built-in marker plugin clamps circle markers to a small set of odd
// integer pixel diameters (see drawCircle / shapeSize in lightweight-
// charts), which limited bubble variety to ~3 distinct visible sizes.
//
// Each instance owns one (series → bubble list) binding. The chart
// fires draw() on every redraw (zoom, pan, resize, data change), so
// updateAllViews + requestUpdate after setPoints is all we need to
// repaint when seriesData changes.

interface BubblePoint {
  time: Time;
  price: number;
  radius: number; // CSS pixels
}

// Convert `#rrggbb` to `rgba(r, g, b, a)`. Only handles 6-digit hex (the
// only form COLOR.* uses); kept inline because it's the sole consumer.
function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

class BubblesPrimitive implements ISeriesPrimitive<Time> {
  private _chart: IChartApi | null = null;
  private _series: ISeriesApi<'Line'> | null = null;
  private _points: BubblePoint[] = [];
  private readonly _colorCenter: string;
  private readonly _colorEdge: string;
  private readonly _paneViews: [BubblesPaneView];

  constructor(color: string) {
    // Opaque hex at the center, fully transparent same-RGB at the edge,
    // so the bubble fades to invisible without leaving a hard outline.
    this._colorCenter = color;
    this._colorEdge = hexToRgba(color, 0);
    this._paneViews = [new BubblesPaneView(this)];
  }

  attached(p: SeriesAttachedParameter<Time>) {
    this._chart = p.chart;
    this._series = p.series as ISeriesApi<'Line'>;
  }

  detached() {
    this._chart = null;
    this._series = null;
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return this._paneViews;
  }

  updateAllViews() {
    // No cached state on the pane view — coordinates are computed
    // fresh inside the renderer, so this is a no-op. The hook exists
    // so the chart knows to re-call renderer() on viewport changes.
  }

  setPoints(points: BubblePoint[]) {
    this._points = points;
  }

  get chart() { return this._chart; }
  get series() { return this._series; }
  get points() { return this._points; }
  get colorCenter() { return this._colorCenter; }
  get colorEdge() { return this._colorEdge; }
}

class BubblesPaneView implements IPrimitivePaneView {
  constructor(private readonly _source: BubblesPrimitive) {}
  renderer(): IPrimitivePaneRenderer {
    return new BubblesRenderer(this._source);
  }
}

class BubblesRenderer implements IPrimitivePaneRenderer {
  constructor(private readonly _source: BubblesPrimitive) {}

  draw(target: CanvasRenderingTarget2D): void {
    const chart = this._source.chart;
    const series = this._source.series;
    if (!chart || !series) return;
    const timeScale = chart.timeScale();
    const points = this._source.points;
    if (points.length === 0) return;

    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context;
      // Use the average pixel ratio for radius scaling so circles stay
      // round even if a browser somehow reports asymmetric ratios.
      const pixelRatio = (scope.horizontalPixelRatio + scope.verticalPixelRatio) / 2;
      const colorCenter = this._source.colorCenter;
      const colorEdge = this._source.colorEdge;
      for (const pt of points) {
        const x = timeScale.timeToCoordinate(pt.time);
        if (x == null) continue;
        const y = series.priceToCoordinate(pt.price);
        if (y == null) continue;
        const cx = x * scope.horizontalPixelRatio;
        const cy = y * scope.verticalPixelRatio;
        const r = pt.radius * pixelRatio;
        // Radial gradient: opaque at the center, fades to fully
        // transparent at the edge. Overlapping bubbles blend
        // additively in their soft halos so dense clusters get a
        // bright core without a hard outline.
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        grad.addColorStop(0, colorCenter);
        grad.addColorStop(1, colorEdge);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, 2 * Math.PI);
        ctx.fill();
      }
    });
  }
}

// ── Live line buffer: only sampled when mode='live'. Reads
//    `priceCents` per venue on every marketStore version bump. The team
//    matters because Kalshi has separate tickers per team — we read the
//    ticker matching the selected team. Poly only has one ticker per
//    game; we flip its price if the selected team isn't the named team.
interface LiveBufferState {
  kalshi: Array<{ time: Time; value: number }>;
  poly: Array<{ time: Time; value: number }>;
}

function useLiveLineBuffer(
  enabled: boolean,
  game: GameRow | null,
  selectedTeam: string,
  lineTickers: { kalshi: string | null; polymarket: string | null } | null,
): LiveBufferState {
  const version = useMarketStore((s) => s.version);
  const bufferRef = useRef<LiveBufferState>({ kalshi: [], poly: [] });
  const [, force] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const nowSec = Math.floor(Date.now() / 1000) as Time;
    let kalshiCents: number | null = null;
    let polyCents: number | null = null;

    if (lineTickers) {
      // Spread/total: sample the selected line's tickers directly via
      // marketStore. getMarketPrice returns YES-cents per ticker; that's
      // already the shape the chart wants.
      const store = useMarketStore.getState();
      if (lineTickers.kalshi) {
        kalshiCents = store.getMarketPrice(lineTickers.kalshi);
      }
      if (lineTickers.polymarket) {
        polyCents = store.getMarketPrice(lineTickers.polymarket);
      }
    } else if (game) {
      // Moneyline: pick yes-price for the selected team from each venue's
      // GameRow book. GameRow.kalshi.away.priceCents is yes-price for the
      // away ticker; home likewise. So when selectedTeam === awayCode we
      // want kalshi.away, else kalshi.home. Poly works the same way —
      // VenueBook is per-team.
      const sel = selectedTeam.toUpperCase();
      const awayCode = game.awayCode.toUpperCase();
      const useAwayBook = sel === awayCode;
      kalshiCents = game.kalshi
        ? (useAwayBook ? game.kalshi.away.priceCents : game.kalshi.home.priceCents)
        : null;
      polyCents = game.polymarket
        ? (useAwayBook
            ? game.polymarket.away.priceCents
            : game.polymarket.home.priceCents)
        : null;
    } else {
      return;
    }

    let dirty = false;
    if (kalshiCents != null && Number.isFinite(kalshiCents)) {
      dirty = appendOrUpdate(bufferRef.current.kalshi, nowSec, kalshiCents) || dirty;
    }
    if (polyCents != null && Number.isFinite(polyCents)) {
      dirty = appendOrUpdate(bufferRef.current.poly, nowSec, polyCents) || dirty;
    }
    if (dirty) force((n) => n + 1);
  }, [version, enabled, game, selectedTeam, lineTickers]);

  // When mode flips off live or the line/team changes, reset so a re-
  // entry doesn't pre-fill with stale points from an earlier session.
  useEffect(() => {
    if (!enabled) {
      bufferRef.current = { kalshi: [], poly: [] };
    }
  }, [enabled, selectedTeam, lineTickers]);

  return bufferRef.current;
}

function appendOrUpdate(
  buf: Array<{ time: Time; value: number }>,
  time: Time,
  value: number,
): boolean {
  const last = buf[buf.length - 1];
  if (last && last.time === time) {
    if (last.value === value) return false;
    last.value = value;
    return true;
  }
  if (last && (last.time as number) > (time as number)) return false;
  buf.push({ time, value });
  return true;
}

export function PriceChart({
  mode,
  trades,
  selectedTeam,
  bothTeams,
  enabledVenues,
  game,
  lineTickers = null,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  // 4 bubble series + 2 optional live-line series. Created lazily based
  // on which venues are enabled — toggling Kalshi/Poly off removes both
  // its bubble series and its live line.
  const bubbleSeriesRef = useRef<Map<SeriesKey, ISeriesApi<'Line'>>>(new Map());
  const bubblePrimitivesRef = useRef<Map<SeriesKey, BubblesPrimitive>>(new Map());
  const liveSeriesRef = useRef<Map<Venue, ISeriesApi<'Line'>>>(new Map());

  // Which view-jump button (Event / All) was last hit. Lit while the
  // chart hasn't been reshaped since; cleared on any user pan/zoom or
  // programmatic refit. The ignore-window swallows the range-change
  // events that our own setVisibleRange/fitContent calls emit (often
  // multiple, asynchronously) so the highlight survives the click.
  const [activeViewButton, setActiveViewButton] = useState<'event' | 'all' | null>(null);
  const ignoreChangeUntilRef = useRef(0);

  const liveBuffer = useLiveLineBuffer(mode === 'live', game, selectedTeam, lineTickers);

  // Event start (from any Kalshi ticker in the dataset) clips the
  // chart's default visible range so archived games open on the in-game
  // window instead of the multi-day pre-game trickle.
  const eventStartMs = useMemo(() => earliestEventStart(trades), [trades]);

  // Bucket projected trades by series key so we can setData per series.
  const seriesData = useMemo(() => {
    const buckets: Record<SeriesKey, Array<{ t: number; price: number; size: number }>> = {
      'kalshi-yes': [],
      'kalshi-no': [],
      'poly-yes': [],
      'poly-no': [],
    };
    if (lineTickers) {
      // Spread/total: strict per-venue ticker filter. Each line chip
      // points to one ticker per venue (or null for venues that don't
      // have a market at this line) — exclude all trades that don't
      // match. Bucket by the trade's native YES/NO; no team-projection
      // since spread/total are single-ticker-per-line markets. The YES
      // bubble cloud and NO bubble cloud naturally separate at the
      // implied mid because trade.price is already side-native.
      const k = lineTickers.kalshi;
      const p = lineTickers.polymarket;
      for (const row of trades) {
        const venue: Venue =
          (row.source ?? '').toLowerCase() === 'polymarket' ? 'polymarket' : 'kalshi';
        if (!enabledVenues.has(venue)) continue;
        if (venue === 'kalshi') {
          if (k == null || row.ticker !== k) continue;
        } else {
          if (p == null || row.ticker !== p) continue;
        }
        // Same block_ts gate as moneyline — see project_hydra_ask_poly_trade_ts_skew.
        if (venue === 'polymarket' && row.block_ts == null) continue;
        const side = (row.side ?? '').toLowerCase();
        if (side !== 'yes' && side !== 'no') continue;
        const venueKey = venue === 'kalshi' ? 'kalshi' : 'poly';
        const key = `${venueKey}-${side}` as SeriesKey;
        const t = Date.parse(row.block_ts ?? row.ts);
        if (!Number.isFinite(t)) continue;
        const size = Number.isFinite(row.size) && row.size > 0 ? row.size : 0;
        buckets[key].push({ t, price: row.price, size });
      }
    } else {
      // Moneyline: project each trade onto the selected team's yes-axis.
      // projectTradeForTeam handles the cross-venue rename-and-flip so
      // a Poly YES on the OTHER team becomes a NO on the selected team.
      for (const row of trades) {
        const proj = projectTradeForTeam(row, selectedTeam, bothTeams);
        if (!proj) continue;
        if (!enabledVenues.has(proj.venue)) continue;
        // Drop Poly rows without canonical block_ts: their `ts` is hydra-
        // receive-time (wall-clock at decode) and during crash-loop
        // backfills batches many distinct on-chain transactions onto a
        // single second, putting bubbles at visibly wrong x positions.
        // Kalshi rows aren't on-chain and always lack block_ts — keep them.
        // See project_hydra_ask_poly_trade_ts_skew memory.
        if (proj.venue === 'polymarket' && proj.row.block_ts == null) continue;
        const venueKey = proj.venue === 'kalshi' ? 'kalshi' : 'poly';
        const key = `${venueKey}-${proj.effectiveSide}` as SeriesKey;
        const t = Date.parse(proj.row.block_ts ?? proj.row.ts);
        if (!Number.isFinite(t)) continue;
        const size = Number.isFinite(row.size) && row.size > 0 ? row.size : 0;
        buckets[key].push({ t, price: proj.priceCents, size });
      }
    }
    return {
      'kalshi-yes': binBySecond(buckets['kalshi-yes']),
      'kalshi-no': binBySecond(buckets['kalshi-no']),
      'poly-yes': binBySecond(buckets['poly-yes']),
      'poly-no': binBySecond(buckets['poly-no']),
    };
  }, [trades, selectedTeam, bothTeams, enabledVenues, lineTickers]);

  // Total contracts across every enabled series — the denominator for
  // each bubble's fraction-of-game. Both Kalshi and Polymarket `size`
  // are contract counts (hydra stores Poly notional separately as
  // `(price/100) * size`), so pooling is apples-to-apples.
  const totalContracts = useMemo(() => {
    let sum = 0;
    for (const key of ['kalshi-yes', 'kalshi-no', 'poly-yes', 'poly-no'] as SeriesKey[]) {
      for (const pt of seriesData[key]) {
        if (pt.size > 0) sum += pt.size;
      }
    }
    return sum;
  }, [seriesData]);

  // ── chart bootstrap ────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth || 800,
      height: containerRef.current.clientHeight || 380,
      layout: {
        background: { color: 'transparent' },
        textColor: '#9ca3af',
        fontSize: 10,
      },
      grid: {
        vertLines: { color: 'rgba(255, 255, 255, 0.04)' },
        horzLines: { color: 'rgba(255, 255, 255, 0.04)' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: 'rgba(255, 255, 255, 0.2)', width: 1, style: 2 },
        horzLine: { color: 'rgba(255, 255, 255, 0.2)', width: 1, style: 2 },
      },
      rightPriceScale: {
        borderColor: 'rgba(255, 255, 255, 0.1)',
      },
      timeScale: {
        borderColor: 'rgba(255, 255, 255, 0.1)',
        timeVisible: true,
        secondsVisible: false,
        // Allow extreme zoom-out (mouse wheel) so users can see the full
        // pre-game→close span. Default minBarSpacing is 0.5px which caps
        // the visible window well short of multi-day archived data.
        minBarSpacing: 0.001,
        rightOffset: 0,
        // lightweight-charts defaults the axis to UTC. The trades table
        // below uses Date.getHours()/getMinutes() (browser local time),
        // so without this override the two surfaces disagreed on what
        // "8:30" meant. Format ticks in local time AND switch to date
        // labels at day/month boundaries — without that, a chart that
        // spans multiple days renders "14:00" labels for different days
        // back-to-back with no visual disambiguation.
        tickMarkFormatter: (
          time: number | { year: number; month: number; day: number },
          tickMarkType: TickMarkType,
        ) => {
          if (typeof time !== 'number') return '';
          const d = new Date(time * 1000);
          if (tickMarkType === TickMarkType.Year) {
            return String(d.getFullYear());
          }
          if (tickMarkType === TickMarkType.Month) {
            return `${MONTH_ABBR[d.getMonth()]} ${d.getFullYear()}`;
          }
          if (tickMarkType === TickMarkType.DayOfMonth) {
            return `${MONTH_ABBR[d.getMonth()]} ${d.getDate()}`;
          }
          return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
        },
      },
      // Crosshair tooltip — same local-time formatter as the axis so
      // the hover readout doesn't disagree with the tick labels above
      // it (or with the trades table below).
      localization: {
        timeFormatter: (time: number | { year: number; month: number; day: number }) => {
          if (typeof time !== 'number') return '';
          const d = new Date(time * 1000);
          const date = `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          const hms = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
          return `${date} ${hms}`;
        },
      },
    });
    chartRef.current = chart;

    // Drop the view-button highlight whenever the visible range changes
    // outside the short ignore window opened by handleLiveClick /
    // handleAllClick. Time-range subscription (not logical-range) fires
    // reliably on small wheel/pan moves too.
    const onRangeChange = () => {
      if (Date.now() < ignoreChangeUntilRef.current) return;
      setActiveViewButton(null);
    };
    chart.timeScale().subscribeVisibleTimeRangeChange(onRangeChange);

    const onResize = () => {
      if (!containerRef.current || !chartRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      if (w > 0 && h > 0) {
        chartRef.current.applyOptions({ width: w, height: h });
      }
    };
    window.addEventListener('resize', onResize);
    const ro = new ResizeObserver(onResize);
    ro.observe(containerRef.current);
    // Belt-and-suspenders: re-measure on the next frame and again 100ms
    // later. Prod builds (no StrictMode warm-up render) can commit the
    // bootstrap effect before flexbox layout has run, so the initial
    // clientWidth/Height come back zero. We seeded sensible fallbacks
    // (800x380) at construction; these post-mount measures snap to the
    // real container size as soon as layout settles.
    const raf = requestAnimationFrame(onResize);
    const timer = window.setTimeout(onResize, 100);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      window.removeEventListener('resize', onResize);
      ro.disconnect();
      chart.timeScale().unsubscribeVisibleTimeRangeChange(onRangeChange);
      chart.remove();
      chartRef.current = null;
      bubbleSeriesRef.current.clear();
      bubblePrimitivesRef.current.clear();
      liveSeriesRef.current.clear();
    };
  }, []);

  // ── bubble series lifecycle: create/destroy on venue toggle ────
  // Returns a counter that bumps whenever the set of live series
  // changes. The data effect depends on it so a fresh-series mount
  // (e.g. after a StrictMode double-mount or chart remove + recreate)
  // immediately re-applies the cached seriesData rather than waiting
  // for the next trades update.
  const [seriesRevision, setSeriesRevision] = useState(0);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const order: SeriesKey[] = ['kalshi-no', 'poly-no', 'kalshi-yes', 'poly-yes'];
    let changed = false;
    for (const key of order) {
      const venue = venueOf(key);
      const shouldExist = enabledVenues.has(venue);
      const existing = bubbleSeriesRef.current.get(key);
      if (shouldExist && !existing) {
        // The thin LineSeries traces the trajectory; the attached
        // BubblesPrimitive draws the circles on top with subpixel
        // radius control (replaces createSeriesMarkers, which clamps
        // circle markers to ~8 distinct pixel diameters).
        const series = chart.addSeries(LineSeries, {
          color: colorOf(key),
          lineWidth: 1,
          lineStyle: LineStyle.Solid,
          priceLineVisible: false,
          lastValueVisible: false,
        });
        // No autoscaleInfoProvider — let lightweight-charts auto-fit
        // the Y-axis to the actual price range, and surface the
        // built-in price-scale interactions (wheel = zoom Y, drag =
        // pan Y, double-click = reset to auto-fit). Pinning the range
        // to 0..100 disables all of that.
        const primitive = new BubblesPrimitive(colorOf(key));
        series.attachPrimitive(primitive);
        bubbleSeriesRef.current.set(key, series);
        bubblePrimitivesRef.current.set(key, primitive);
        changed = true;
      } else if (!shouldExist && existing) {
        const primitive = bubblePrimitivesRef.current.get(key);
        if (primitive) existing.detachPrimitive(primitive);
        chart.removeSeries(existing);
        bubbleSeriesRef.current.delete(key);
        bubblePrimitivesRef.current.delete(key);
        changed = true;
      }
    }
    if (changed) setSeriesRevision((n) => n + 1);
  }, [enabledVenues]);

  // ── bubble data ────────────────────────────────────────────────
  // Each render of seriesData (every pagination batch) replaces each
  // series' point list with the cumulative set so far. We then compute
  // the actual time span across all series and force the visible range
  // to it — fitContent alone proved unreliable when called right after
  // setData. Apply both synchronously and on rAF so at least one sticks
  // even if the chart hasn't finished digesting the new data.
  useEffect(() => {
    const order: SeriesKey[] = ['kalshi-yes', 'kalshi-no', 'poly-yes', 'poly-no'];
    for (const key of order) {
      const series = bubbleSeriesRef.current.get(key);
      const primitive = bubblePrimitivesRef.current.get(key);
      if (!series || !primitive) continue;
      // Pass only the fields lightweight-charts expects on a line point;
      // pt.size is metadata used by the BubblesPrimitive for sizing.
      series.setData(seriesData[key].map((pt) => ({ time: pt.time, value: pt.value })));
      primitive.setPoints(
        seriesData[key].map((pt) => ({
          time: pt.time,
          price: pt.value,
          radius: bubbleRadius(pt.size, totalContracts),
        })),
      );
    }
    let minT = Number.POSITIVE_INFINITY;
    let maxT = Number.NEGATIVE_INFINITY;
    for (const key of order) {
      for (const pt of seriesData[key]) {
        const t = pt.time as number;
        if (t < minT) minT = t;
        if (t > maxT) maxT = t;
      }
    }
    // In live mode, extend the right edge to "now" so the WS-fed live
    // line has room to render past the most recent bubble (analytics
    // trades typically lag the live feed by seconds-to-minutes).
    if (mode === 'live') {
      const nowSec = Math.floor(Date.now() / 1000);
      if (nowSec > maxT) maxT = nowSec;
    }
    // For archived games, prefer the actual event start time over the
    // raw earliest-trade time — markets often open days before the game
    // and the pre-game trickle squashes the in-game action against the
    // right edge. Pulled from any Kalshi game ticker in the dataset;
    // falls back to maxT - 8h when no ticker carries it.
    if (eventStartMs != null) {
      const eventStartSec = Math.floor(eventStartMs / 1000);
      if (eventStartSec > minT && eventStartSec < maxT) {
        minT = eventStartSec;
      }
    } else if (Number.isFinite(maxT)) {
      const eightHoursBack = maxT - 8 * 3600;
      if (eightHoursBack > minT) minT = eightHoursBack;
    }
    const hasSpan =
      Number.isFinite(minT) && Number.isFinite(maxT) && minT < maxT;
    const apply = () => {
      const chart = chartRef.current;
      if (!chart) return;
      if (hasSpan) {
        chart.timeScale().setVisibleRange({
          from: minT as Time,
          to: maxT as Time,
        });
      } else {
        chart.timeScale().fitContent();
      }
    };
    apply();
    const id = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(id);
  }, [seriesData, seriesRevision, totalContracts, mode, eventStartMs]);

  // ── live-line series lifecycle: only when mode='live' AND venue on ─
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const desired: Venue[] = mode === 'live'
      ? (['kalshi', 'polymarket'] as Venue[]).filter((v) => enabledVenues.has(v))
      : [];
    // Add missing
    for (const v of desired) {
      if (liveSeriesRef.current.has(v)) continue;
      const series = chart.addSeries(LineSeries, {
        color: v === 'kalshi' ? COLOR.kalshi.live : COLOR.poly.live,
        lineWidth: 2,
        lineStyle: LineStyle.Solid,
        priceLineVisible: false,
        lastValueVisible: false,
      });
      // No autoscaleInfoProvider — share the bubble series' auto-fit
      // behavior so the Y-axis stays consistent across both lanes.
      liveSeriesRef.current.set(v, series);
    }
    // Remove no-longer-desired
    for (const [v, s] of liveSeriesRef.current.entries()) {
      if (!desired.includes(v)) {
        chart.removeSeries(s);
        liveSeriesRef.current.delete(v);
      }
    }
  }, [mode, enabledVenues]);

  // ── live-line data ─────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'live') return;
    const k = liveSeriesRef.current.get('kalshi');
    const p = liveSeriesRef.current.get('polymarket');
    if (k) k.setData(liveBuffer.kalshi);
    if (p) p.setData(liveBuffer.poly);
  }, [mode, liveBuffer.kalshi.length, liveBuffer.poly.length]);

  // Event window for the "EVENT" jump-to button: kickoff (or earliest
  // trade if Kalshi tickers don't carry a start time) → last trade.
  // Null when we have no data to zoom to.
  const eventWindow = useMemo<{ from: Time; to: Time } | null>(() => {
    let maxT = Number.NEGATIVE_INFINITY;
    for (const key of ['kalshi-yes', 'kalshi-no', 'poly-yes', 'poly-no'] as SeriesKey[]) {
      for (const pt of seriesData[key]) {
        const t = pt.time as number;
        if (t > maxT) maxT = t;
      }
    }
    // Live mode: extend right edge to "now" so EVENT jumps to
    // kickoff → now, not kickoff → last bubble (which lags reality).
    if (mode === 'live') {
      const nowSec = Math.floor(Date.now() / 1000);
      if (nowSec > maxT) maxT = nowSec;
    }
    if (!Number.isFinite(maxT)) return null;
    const startSec = eventStartMs != null ? Math.floor(eventStartMs / 1000) : null;
    const from = startSec != null && startSec < maxT ? startSec : maxT - 8 * 3600;
    if (!(from < maxT)) return null;
    return { from: from as Time, to: maxT as Time };
  }, [seriesData, eventStartMs, mode]);

  const handleLiveClick = () => {
    if (!chartRef.current || !eventWindow) return;
    ignoreChangeUntilRef.current = Date.now() + 300;
    chartRef.current.timeScale().setVisibleRange(eventWindow);
    setActiveViewButton('event');
  };

  const handleAllClick = () => {
    if (!chartRef.current) return;
    ignoreChangeUntilRef.current = Date.now() + 300;
    chartRef.current.timeScale().fitContent();
    setActiveViewButton('all');
  };

  const noTrades = trades.length === 0;
  const totalsByKey = {
    'kalshi-yes': seriesData['kalshi-yes'].length,
    'kalshi-no': seriesData['kalshi-no'].length,
    'poly-yes': seriesData['poly-yes'].length,
    'poly-no': seriesData['poly-no'].length,
  };
  const totalBubbles =
    totalsByKey['kalshi-yes'] +
    totalsByKey['kalshi-no'] +
    totalsByKey['poly-yes'] +
    totalsByKey['poly-no'];

  return (
    <div className="relative flex h-[420px] flex-col">
      <div className="text-muted-foreground mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px]">
        {enabledVenues.has('kalshi') && (
          <>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: COLOR.kalshi.yes }}
              />
              Kalshi YES ({totalsByKey['kalshi-yes']})
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: COLOR.kalshi.no }}
              />
              Kalshi NO ({totalsByKey['kalshi-no']})
            </span>
          </>
        )}
        {enabledVenues.has('polymarket') && (
          <>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: COLOR.poly.yes }}
              />
              Poly YES ({totalsByKey['poly-yes']})
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: COLOR.poly.no }}
              />
              Poly NO ({totalsByKey['poly-no']})
            </span>
          </>
        )}
        <span className="ml-auto">
          Y-axis: yes-price ¢ for {selectedTeam.toUpperCase()}
        </span>
      </div>
      <div ref={containerRef} className="min-h-0 flex-1" />
      {eventWindow && (
        <div className="mt-1 flex gap-1.5">
          <button
            type="button"
            onClick={handleLiveClick}
            title="Fit chart to kickoff → last trade"
            className={viewButtonClass(activeViewButton === 'event')}
          >
            Event
          </button>
          <button
            type="button"
            onClick={handleAllClick}
            title="Fit chart to every trade (full pre-game → close span)"
            className={viewButtonClass(activeViewButton === 'all')}
          >
            All
          </button>
        </div>
      )}
      {noTrades && (
        <div className="text-muted-foreground pointer-events-none absolute inset-0 flex items-center justify-center text-[11px]">
          No analytics trades returned for this game
        </div>
      )}
      {!noTrades && totalBubbles === 0 && (
        <div className="text-muted-foreground pointer-events-none absolute inset-0 flex items-center justify-center text-[11px]">
          No bubbles matched the current toolbar selection
        </div>
      )}
      {mode === 'live' && !game && (
        <div className="text-amber-500/80 pointer-events-none absolute bottom-2 right-3 text-[10px]">
          Live tickers not in marketStore — line will populate once feed catches up
        </div>
      )}
    </div>
  );
}

// Shared style for the view-jump buttons (Event / All): muted grey by
// default, bright cyan when that button represents the current view.
function viewButtonClass(active: boolean): string {
  const base =
    'rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-colors';
  return active
    ? `${base} border-cyan-400 bg-cyan-500/30 text-cyan-100 hover:bg-cyan-500/40`
    : `${base} border-white/15 bg-white/5 text-white/70 hover:bg-white/10`;
}
